/**
 * PickALeaf global cart drawer.
 *
 * Single source of truth for: opening/closing the drawer, refreshing cart
 * state, quantity changes, cart-count badges, and mobile scroll locking.
 *
 * ---------------------------------------------------------------------------
 * Performance rewrite. What changed and why
 * ---------------------------------------------------------------------------
 *
 * 1. ADD TO CART IS NOW ONE NETWORK ROUND TRIP, NOT THREE.
 *
 *    Previously: POST /cart/add.js  ->  GET /cart.js  ->  GET /cart?section_id=…
 *    all strictly sequential, and only then did the panel appear. On a mobile
 *    connection that is three RTTs before anything visible happens, which is
 *    why the drawer effectively always waited out the 700ms skeleton timer.
 *
 *    Now the add request itself asks Shopify for the re-rendered drawer section
 *    (`sections` parameter on /cart/add.js). The response carries the authoritative
 *    post-add markup, so `applyAddResponse()` swaps it in and opens the panel with
 *    zero further requests. Nothing is faked: the HTML is server-rendered from the
 *    real cart, exactly as the section-render endpoint used to return.
 *
 * 2. OPENING THE DRAWER NO LONGER BLOCKS ON THE NETWORK WHEN THE DOM IS KNOWN GOOD.
 *
 *    The drawer markup is rendered by Liquid from the live cart on every page
 *    load, and every mutation this script performs re-applies verified server
 *    markup. That state is tracked in `drawerFresh`. When fresh, the panel opens
 *    on the click and revalidates in the background; when stale (a cart:updated
 *    event, a bfcache restore, a failed refresh) it falls back to the awaited
 *    path so the customer can never be shown a cart that is out of date.
 *
 * 3. REMOVED A PERMANENTLY-RUNNING, ALWAYS-DEAD OBSERVER.
 *
 *    `[data-pf-global-cart-fallback]` is not present anywhere in this theme, so
 *    `syncFallbackTrigger()` was a no-op — but the childList+subtree
 *    MutationObserver over the header and #MainContent that drove it, plus its
 *    resize listener, ran on every page regardless. Both are gone.
 *
 * 4. `setCartCount` no longer runs URL parsing over every anchor on the page.
 *    A cheap substring pre-filter skips the ~99% of links that cannot be the
 *    cart before any `new URL()` work happens.
 *
 * Behaviours deliberately preserved from the previous build:
 *   - Refresh always runs; the first open of a page session is not skipped.
 *   - Concurrent refreshes are deduped and sequence-stamped so a slow response
 *     can never overwrite newer content.
 *   - /cart.js is the guaranteed fallback renderer: the drawer must never show
 *     the empty state while the cart has items.
 *
 * Public API, for Add to Cart callers:
 *   window.PickAleafCart.refresh()                -> Promise<cart>
 *   window.PickAleafCart.open(trigger)            -> Promise<void>
 *   window.PickAleafCart.openAfterAdd(trigger)    -> Promise<void>  (awaits fresh data)
 *   window.PickAleafCart.applyAddResponse(data)   -> boolean  (one-round-trip path)
 *   window.PickAleafCart.sectionId()              -> string
 *   window.PickAleafCart.close() / .isOpen()
 * Callers may also simply dispatch `cart:updated` on document.
 */
(() => {
  if (window.__pickaleafGlobalCartReady) return;
  window.__pickaleafGlobalCartReady = true;

  const drawer =
    document.querySelector('[data-pf-global-cart-drawer]') ||
    document.querySelector('[data-pf-cart-drawer]');
  if (!drawer) return;

  const body = document.body;
  const html = document.documentElement;

  const shopRoot =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const route = (path) => `${shopRoot.replace(/\/?$/, '/')}${path.replace(/^\//, '')}`;

  const cartUrl = route('cart');
  const cartChangeUrl = route('cart/change.js');
  const cartJsUrl = `${cartUrl.replace(/\/+$/, '')}.js`;
  const cartPath =
    new URL(cartUrl, window.location.origin).pathname.replace(/\/+$/, '') || '/cart';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const isMobile = window.matchMedia('(max-width: 749px)');

  const drawerSectionId = drawer.dataset.pfCartSectionId || 'pickaleaf-cart-drawer';

  let lastTrigger = null;
  let mutatingQuantity = false;
  let closeTimer = 0;
  let eventTimer = 0;

  let scrollLocked = false;
  let scrollY = 0;
  let savedStyles = null;

  let inFlight = null;
  let requestSeq = 0;
  let appliedSeq = 0;

  /* The drawer DOM currently matches server state. True at load (Liquid rendered
     it from the live cart) and after every verified apply. */
  let drawerFresh = true;

  /* ---------------- mobile scroll lock ---------------- */

  const lockScroll = () => {
    if (!isMobile.matches || scrollLocked) return;
    scrollY = window.scrollY || html.scrollTop || body.scrollTop || 0;
    savedStyles = {
      htmlOverflow: html.style.overflow,
      htmlHeight: html.style.height,
      htmlOverscrollBehavior: html.style.overscrollBehavior,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
      bodyOverflow: body.style.overflow,
      bodyOverscrollBehavior: body.style.overscrollBehavior,
    };
    html.style.overflow = 'hidden';
    html.style.height = '100%';
    html.style.overscrollBehavior = 'none';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';
    scrollLocked = true;
  };

  const unlockScroll = () => {
    if (!scrollLocked) return;
    const restoreTo = scrollY;
    const s = savedStyles || {};
    html.style.overflow = s.htmlOverflow || '';
    html.style.height = s.htmlHeight || '';
    html.style.overscrollBehavior = s.htmlOverscrollBehavior || '';
    body.style.position = s.bodyPosition || '';
    body.style.top = s.bodyTop || '';
    body.style.left = s.bodyLeft || '';
    body.style.right = s.bodyRight || '';
    body.style.width = s.bodyWidth || '';
    body.style.overflow = s.bodyOverflow || '';
    body.style.overscrollBehavior = s.bodyOverscrollBehavior || '';
    scrollLocked = false;
    savedStyles = null;
    scrollY = 0;
    window.requestAnimationFrame(() => window.scrollTo(0, restoreTo));
  };

  /* ---------------- helpers ---------------- */

  const isCartLink = (anchor) => {
    if (!anchor || anchor.hasAttribute('data-pf-cart-bypass')) return false;
    /* Cheap reject before any URL parsing: the cart path always contains "cart".
       This keeps setCartCount off the hot path on collection pages, which carry
       a few hundred anchors. */
    const raw = anchor.getAttribute('href');
    if (!raw || raw.indexOf('cart') === -1) return false;
    try {
      const url = new URL(anchor.href, window.location.href);
      const path = url.pathname.replace(/\/+$/, '') || '/';
      return url.origin === window.location.origin && path === cartPath;
    } catch {
      return false;
    }
  };

  const setCartCount = (value) => {
    const count = Math.max(0, Number(value || 0));

    document.querySelectorAll('[data-pf-cart-count]').forEach((node) => {
      node.textContent = count;
      node.classList.toggle('is-empty', count === 0);
    });

    document.querySelectorAll('.pickaleaf-gateway__cart').forEach((node) => {
      let badge = node.querySelector('.pickaleaf-gateway__cart-count');
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'pickaleaf-gateway__cart-count';
        badge.setAttribute('data-pf-cart-count', '');
        node.appendChild(badge);
      }
      badge.textContent = count;
      badge.classList.toggle('is-empty', count === 0);
      node.setAttribute('aria-label', `Cart, ${count} items`);
    });

    document.querySelectorAll('a[href*="cart"]').forEach((anchor) => {
      if (!isCartLink(anchor)) return;
      anchor.setAttribute('aria-label', `Cart, ${count} items`);
      anchor.querySelectorAll('span').forEach((span) => {
        if (/^\s*Cart(?:\s*\(\d+\))?\s*$/i.test(span.textContent || '')) {
          span.textContent = count > 0 ? `Cart (${count})` : 'Cart';
        }
      });
    });
  };

  const sectionUrl = () => {
    const url = new URL(cartUrl, window.location.origin);
    const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
    url.searchParams.set('section_id', drawerSectionId);
    if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
    return url.toString();
  };

  /* ---------------- rendering ---------------- */

  const money = (cents) => {
    const value = (Number(cents || 0) / 100).toFixed(2);
    return 'Rs.' + value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    );

  /**
   * Swap in server-rendered drawer markup.
   *
   * Accepts the HTML returned either by the `sections` field of /cart/add.js or
   * by the ?section_id= endpoint; both render the same section. Returns the
   * post-swap item count, or null when the markup was not usable.
   */
  const swapRegion = (selector, from) => {
    const to = drawer.querySelector(selector);
    const src = from.querySelector(selector);
    if (!to || !src) return false;
    to.innerHTML = src.innerHTML;
    return true;
  };

  const applySectionHTML = (sectionHTML) => {
    if (!sectionHTML) return null;
    let next = null;
    try {
      const parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
      next =
        parsed.querySelector('[data-pf-global-cart-drawer]') ||
        parsed.querySelector('[data-pf-cart-drawer]');
    } catch (_) {
      return null;
    }
    if (!next) return null;

    /* Only three regions ever differ between two renders of this section: the
       item list, the totals, and the count. Replacing drawer.innerHTML swapped
       all ~81 nodes every time - the background layers, the trust icons and the
       checkout button included, none of which change. Every inserted node is
       work for anything on the page that watches the DOM, and detaching the
       checkout button meant it had to be re-claimed after each update.

       Targeted swap is ~20 nodes and never touches the <form> that holds the
       checkout button. If the markup ever stops matching, it falls back to the
       old wholesale replace so the drawer is still correct. */
    const swapped =
      swapRegion('[data-pf-cart-drawer-body], .pf-cart-drawer__body', next) &&
      swapRegion('.pf-cart-drawer__totals', next);

    if (swapped) {
      /* Carries both the number and the ITEM / ITEMS wording. */
      swapRegion('.pf-cart-drawer__piece-count', next);
      const footer = drawer.querySelector('[data-pf-cart-drawer-footer], .pf-cart-drawer__footer');
      const nextFooter = next.querySelector('[data-pf-cart-drawer-footer], .pf-cart-drawer__footer');
      if (footer && nextFooter) {
        footer.classList.toggle('is-empty', nextFooter.classList.contains('is-empty'));
      }
    } else {
      drawer.innerHTML = next.innerHTML;
    }

    const count = Number(next.dataset.pfCartCountValue);
    if (Number.isFinite(count)) {
      drawer.dataset.pfCartCountValue = String(count);
      return count;
    }
    return Number(drawer.dataset.pfCartCountValue || 0);
  };

  /**
   * Render the item list straight from /cart.js.
   *
   * The section markup is the nicer source, but it is allowed to fail. /cart.js
   * already carries everything the list needs, so it is the guaranteed fallback:
   * a failure upstream must never leave a paying customer looking at the empty
   * state immediately after a successful add.
   */
  const renderItemsFromCart = (cart) => {
    const bodyEl = drawer.querySelector('[data-pf-cart-drawer-body], .pf-cart-drawer__body');
    if (!bodyEl) return false;

    const items = Array.isArray(cart.items) ? cart.items : [];
    if (!items.length) return false;

    const rows = items
      .map((item, i) => {
        const line = i + 1;
        const img = item.image
          ? '<img src="' + esc(item.image) + '" alt="' + esc(item.product_title || item.title) + '" loading="lazy">'
          : '';
        const variant =
          item.variant_title && item.variant_title !== 'Default Title'
            ? '<p class="pf-cart-drawer__variant">' + esc(item.variant_title) + '</p>'
            : '';
        return (
          '<li class="pf-cart-drawer__item" data-pf-cart-line="' + line + '">' +
          '<a class="pf-cart-drawer__media" href="' + esc(item.url) + '">' +
          '<span class="pf-cart-drawer__media-light" aria-hidden="true"></span>' +
          '<span class="pf-cart-drawer__media-halo" aria-hidden="true"></span>' +
          img +
          '<span class="pf-cart-drawer__media-floor" aria-hidden="true"></span>' +
          '</a>' +
          '<div class="pf-cart-drawer__details">' +
          '<a class="pf-cart-drawer__title" href="' + esc(item.url) + '">' +
          esc(item.product_title || item.title) +
          '</a>' +
          variant +
          '<div class="pf-cart-drawer__price-wrap">' +
          '<p class="pf-cart-drawer__price">' + money(item.final_line_price != null ? item.final_line_price : item.line_price) + '</p>' +
          '</div>' +
          '<div class="pf-cart-drawer__quantity" aria-label="Quantity">' +
          '<button type="button" data-pf-cart-change data-line="' + line + '" data-quantity="' + (item.quantity - 1) + '" aria-label="Decrease quantity">−</button>' +
          '<span>' + item.quantity + '</span>' +
          '<button type="button" data-pf-cart-change data-line="' + line + '" data-quantity="' + (item.quantity + 1) + '" aria-label="Increase quantity">+</button>' +
          '</div></div></li>'
        );
      })
      .join('');

    bodyEl.innerHTML = '<ul class="pf-cart-drawer__items" role="list">' + rows + '</ul>';

    // The footer is rendered even for an empty cart, carrying `is-empty` and
    // zeroed totals. Since only the body was replaced here, clear that stale
    // state too or the checkout button stays hidden and the total reads 0.
    const footer = drawer.querySelector('[data-pf-cart-drawer-footer], .pf-cart-drawer__footer');
    if (footer) {
      footer.classList.remove('is-empty');
      const total = footer.querySelector('[data-pf-cart-total]');
      if (total) total.textContent = money(cart.total_price);
      footer.querySelectorAll('.pf-cart-drawer__summary-row strong').forEach((el, i) => {
        if (i === 0) el.textContent = money(cart.items_subtotal_price != null ? cart.items_subtotal_price : cart.total_price);
      });
    }
    return true;
  };

  /* ---------------- refresh ---------------- */

  const fetchJson = (url, init) =>
    fetch(url, Object.assign({ credentials: 'same-origin', cache: 'no-store' }, init));

  /**
   * Two requests, issued in PARALLEL rather than in series. /cart.js is the
   * source of truth and decides correctness; the section render is the preferred
   * presentation. Firing them together halves the wall-clock cost of a refresh
   * on the paths that still need one (quantity change, revalidate, cart:updated).
   */
  const fetchCart = async () => {
    const seq = ++requestSeq;

    const cartPromise = fetchJson(cartJsUrl, { headers: { Accept: 'application/json' } });
    const sectionPromise = fetchJson(sectionUrl(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((res) => (res.ok ? res.text() : null))
      .catch(() => null);

    const cartRes = await cartPromise;
    if (!cartRes.ok) throw new Error('Unable to read cart');
    const cart = await cartRes.json();

    let sectionHTML = await sectionPromise;

    /* One retry for the presentation layer only, and only if the cart is not
       empty — an empty cart renders correctly from the fallback anyway. */
    if (sectionHTML === null && Number(cart.item_count) > 0) {
      sectionHTML = await fetchJson(sectionUrl(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then((res) => (res.ok ? res.text() : null))
        .catch(() => null);
    }

    /* A response that started before a newer one must never win. */
    if (seq < appliedSeq) return cart;
    appliedSeq = seq;

    const applied = applySectionHTML(sectionHTML) !== null;

    // Guarantee: the drawer never shows the empty state while the cart has
    // items, no matter which of the two sources failed.
    const showsEmpty = !!drawer.querySelector('.pf-cart-drawer__empty');
    const hasRows = !!drawer.querySelector('.pf-cart-drawer__item:not(.pf-cart-skeleton)');
    if (Number(cart.item_count) > 0 && (!applied || showsEmpty || !hasRows)) {
      renderItemsFromCart(cart);
    }

    drawer.dataset.pfCartCountValue = String(cart.item_count || 0);
    setCartCount(cart.item_count);
    drawerFresh = true;
    return cart;
  };

  /**
   * force: true is required after any cart mutation. Without it a caller can
   * join a refresh that was issued BEFORE the mutation and resolve against
   * pre-mutation data.
   */
  const refresh = ({ force = false } = {}) => {
    if (inFlight && !force) return inFlight;

    const run = () => {
      drawer.classList.add('is-updating');
      const p = fetchCart()
        .catch((error) => {
          drawerFresh = false;
          throw error;
        })
        .finally(() => {
          if (inFlight === p) inFlight = null;
          drawer.classList.remove('is-updating');
        });
      inFlight = p;
      return p;
    };

    if (inFlight && force) {
      return inFlight.catch(() => {}).then(run);
    }
    return run();
  };

  /* ---------------- open / close ---------------- */

  const finishClose = () => {
    window.clearTimeout(closeTimer);
    drawer.classList.remove('is-closing');
    if (drawer.open) drawer.close();
    body.classList.remove('is-cart-open');
    unlockScroll();
    if (lastTrigger && document.contains(lastTrigger) && typeof lastTrigger.focus === 'function') {
      window.setTimeout(() => lastTrigger.focus({ preventScroll: true }), 0);
    }
  };

  const closeDrawer = () => {
    if (!drawer.open || drawer.classList.contains('is-closing')) return;
    drawer.classList.add('is-closing');
    closeTimer = window.setTimeout(finishClose, reduceMotion.matches ? 0 : 430);
  };

  const showDrawer = (trigger) => {
    window.clearTimeout(closeTimer);
    drawer.classList.remove('is-closing');
    lastTrigger = trigger || document.activeElement;

    if (!drawer.open) {
      try {
        drawer.showModal(); 
        document.dispatchEvent(new CustomEvent('cart:open'));
      } catch {
        drawer.setAttribute('open', '');
      }
    }

    body.classList.add('is-cart-open');
    lockScroll();
    window.setTimeout(
      () => drawer.querySelector('[data-pf-cart-close]')?.focus({ preventScroll: true }),
      150
    );
  };

  /* How long the drawer will wait for real data before opening with placeholder
     rows instead. The one-round-trip add path normally beats this outright, so
     on a decent connection no placeholder is ever seen. On a slow connection the
     customer still gets a panel ~120ms after their tap rather than a button that
     looks dead — which was the actual complaint. Nothing here reports success:
     the real line items replace the placeholder the moment the server responds,
     and a failed add still surfaces its error. */
  const SHOW_SKELETON_MS = 120;
  const SKELETON_ROWS = 2;

  const SKELETON_ROW =
    '<li class="pf-cart-drawer__item pf-cart-skeleton" aria-hidden="true">' +
    '<span class="pf-cart-skeleton__media"></span>' +
    '<span class="pf-cart-skeleton__lines">' +
    '<span class="pf-cart-skeleton__line"></span>' +
    '<span class="pf-cart-skeleton__line pf-cart-skeleton__line--short"></span>' +
    '</span></li>';

  const renderSkeleton = () => {
    const bodyEl = drawer.querySelector('[data-pf-cart-drawer-body], .pf-cart-drawer__body');
    if (!bodyEl) return;

    const existing = bodyEl.querySelector('.pf-cart-drawer__items');
    const rows = Math.min(Math.max(existing ? existing.children.length : SKELETON_ROWS, 1), 4);

    // Built unconditionally: when the cart was previously empty there is no
    // <ul> in the markup at all, and the drawer would otherwise open showing
    // "Nothing gathered yet" straight after a successful add.
    bodyEl.innerHTML =
      '<ul class="pf-cart-drawer__items pf-cart-drawer__items--loading" role="list">' +
      SKELETON_ROW.repeat(rows) +
      '</ul>';

    drawer.querySelectorAll('[data-pf-cart-count]').forEach((node) => {
      if (node.dataset.pfCountSnapshot === undefined) {
        node.dataset.pfCountSnapshot = node.textContent;
      }
      node.textContent = '';
    });
    drawer.classList.add('is-loading-contents');
    drawer.setAttribute('aria-busy', 'true');
  };

  const clearSkeleton = () => {
    drawer.querySelectorAll('[data-pf-cart-count]').forEach((node) => {
      if (node.dataset.pfCountSnapshot !== undefined) delete node.dataset.pfCountSnapshot;
    });
    drawer.classList.remove('is-loading-contents');
    drawer.removeAttribute('aria-busy');
  };

  /**
   * The awaited open. Refresh completes BEFORE the panel is shown, so the panel
   * can never appear carrying stale line items, a stale count, or the empty
   * state after a successful add. Used whenever `drawerFresh` is false.
   */
  const openAwaited = (trigger) => {
    let shown = false;
    const showWithSkeleton = () => {
      if (shown) return;
      shown = true;
      renderSkeleton();
      showDrawer(trigger);
    };

    const slowNetwork = window.setTimeout(showWithSkeleton, SHOW_SKELETON_MS);

    return refresh({ force: true })
      .then(() => {
        window.clearTimeout(slowNetwork);
        clearSkeleton();
        if (!shown) {
          shown = true;
          showDrawer(trigger);
        }
      })
      .catch(() => {
        window.clearTimeout(slowNetwork);
        // Deliberately not restoring the pre-open DOM: after an add that is
        // the empty state, and showing it would tell the customer the add
        // failed when it did not. Keep the placeholder and retry.
        if (!shown) {
          shown = true;
          renderSkeleton();
          showDrawer(trigger);
        }
        window.setTimeout(() => {
          refresh({ force: true }).then(clearSkeleton).catch(() => {});
        }, 600);
      });
  };

  /**
   * Public open. When the drawer DOM is known to match server state the panel
   * appears on the click itself and revalidation runs behind it; otherwise the
   * awaited path is used.
   */
  const openDrawer = (trigger, { refresh: shouldRefresh = true } = {}) => {
    if (!shouldRefresh) {
      showDrawer(trigger);
      return Promise.resolve();
    }

    if (drawerFresh && !drawer.classList.contains('is-loading-contents')) {
      showDrawer(trigger);
      refresh({ force: true }).catch(() => {});
      return Promise.resolve();
    }

    return openAwaited(trigger);
  };

  /**
   * One-round-trip Add to Cart.
   *
   * `data` is the parsed /cart/add.js response requested with the `sections`
   * parameter. Returns true when the drawer was populated and shown from that
   * response alone. Returning false means the caller should fall back to
   * `openAfterAdd()`, which fetches and awaits fresh state.
   */
  const applyAddResponse = (data, trigger) => {
    const sectionHTML = data && data.sections && data.sections[drawerSectionId];
    if (!sectionHTML) return false;

    /* Claim the newest sequence so a refresh already in flight cannot land on
       top of this markup. */
    const seq = ++requestSeq;
    const count = applySectionHTML(sectionHTML);
    if (count === null) return false;
    appliedSeq = seq;

    clearSkeleton();
    setCartCount(count);
    drawerFresh = true;
    showDrawer(trigger);
    return true;
  };

  /* ---------------- quantity ---------------- */

  const changeQuantity = async (line, quantity, control) => {
    if (mutatingQuantity) return;
    mutatingQuantity = true;
    drawerFresh = false;
    drawer.classList.add('is-updating');
    control?.setAttribute('aria-busy', 'true');

    try {
      /* Same one-round-trip shape as Add to Cart: /cart/change.js re-renders the
         drawer section for us, so a quantity step costs one request instead of
         the previous three (change -> cart.js -> section render). The response
         is the full cart object, so item_count is authoritative here. */
      const response = await fetch(cartChangeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          line: Number(line),
          quantity: Math.max(0, Number(quantity)),
          sections: drawerSectionId,
          sections_url: window.location.pathname + window.location.search,
        }),
      });
      if (!response.ok) throw new Error('Unable to update cart');

      let handled = false;
      try {
        const data = await response.json();
        const html = data && data.sections && data.sections[drawerSectionId];
        if (html) {
          const seq = ++requestSeq;
          if (applySectionHTML(html) !== null) {
            appliedSeq = seq;
            setCartCount(Number.isFinite(Number(data.item_count)) ? data.item_count : 0);
            drawer.dataset.pfCartCountValue = String(data.item_count || 0);
            drawerFresh = true;
            handled = true;
          }
        }
      } catch (_) {
        /* fall through to the authoritative refresh below */
      }

      if (!handled) await refresh({ force: true });
    } catch (error) {
      console.error('[PickAleaf cart]', error);
    } finally {
      mutatingQuantity = false;
      drawer.classList.remove('is-updating');
      control?.removeAttribute('aria-busy');
    }
  };

  /* ---------------- events ---------------- */

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const change = target.closest('[data-pf-cart-change]');
      if (change && drawer.contains(change)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        changeQuantity(change.dataset.line, change.dataset.quantity, change);
        return;
      }

      const close = target.closest('[data-pf-cart-close]');
      if (close && drawer.contains(close)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        closeDrawer();
        return;
      }

      const open = target.closest('[data-pf-cart-open], .pickaleaf-gateway__cart');
      if (open) {
        event.preventDefault();
        event.stopImmediatePropagation();
        openDrawer(open);
        return;
      }

      const anchor = target.closest('a[href]');
      if (anchor && isCartLink(anchor)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        openDrawer(anchor);
      }
    },
    true
  );

  /* Anything outside this script that mutates the cart announces it here. The
     DOM is marked stale first so a click arriving before the refresh resolves
     takes the awaited path. */
  const onCartEvent = () => {
    drawerFresh = false;
    window.clearTimeout(eventTimer);
    eventTimer = window.setTimeout(() => {
      refresh({ force: true }).catch((error) => console.warn('[PickAleaf cart event refresh]', error));
    }, 0);
  };
  document.addEventListener('cart:updated', onCartEvent);
  document.addEventListener('cart:refresh', onCartEvent);

  document.addEventListener(
    'touchmove',
    (event) => {
      if (!scrollLocked || !drawer.open) return;
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('.pf-cart-drawer__body')) return;
      event.preventDefault();
    },
    { passive: false, capture: true }
  );

  drawer.addEventListener('click', (event) => {
    if (event.target === drawer) closeDrawer();
  });
  drawer.addEventListener('cancel', (event) => {
    event.preventDefault();
    closeDrawer();
  });
  drawer.addEventListener('close', () => {
    window.clearTimeout(closeTimer);
    drawer.classList.remove('is-closing');
    body.classList.remove('is-cart-open');
    unlockScroll();
  });

  const onBreakpointChange = () => {
    if (!drawer.open) return;
    if (isMobile.matches) lockScroll();
    else unlockScroll();
  };
  if (typeof isMobile.addEventListener === 'function') {
    isMobile.addEventListener('change', onBreakpointChange);
  } else if (typeof isMobile.addListener === 'function') {
    isMobile.addListener(onBreakpointChange);
  }

  /* Back/forward restore: the page comes back exactly as it was cached, which
     may predate a cart change made on the page the customer navigated to. Mark
     stale and revalidate; also make sure a drawer that was open when the
     customer navigated away is not restored open over the page. */
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    drawerFresh = false;
    if (drawer.open) finishClose();
    refresh({ force: true }).catch(() => {});
  });

  window.PickAleafCart = {
    open: (trigger) => openDrawer(trigger),
    // Explicit alias for "I just changed the cart, show me the result".
    openAfterAdd: (trigger) => openAwaited(trigger),
    applyAddResponse,
    sectionId: () => drawerSectionId,
    close: closeDrawer,
    refresh,
    isOpen: () => drawer.open === true,
  };

  setCartCount(Number(drawer.dataset.pfCartCountValue || 0));
})();
